import * as React from 'react';

import { Check } from 'lucide-react';

import { cn } from '../../lib/utils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../dropdown-menu';

import type { VariableChipProps } from './types';

/**
 * VariableChip - An inline chip that displays a variable's current value.
 * Clicking the chip opens a dropdown to select from all possible values.
 * Used within VariableTextarea to render editable variable placeholders.
 */
export const VariableChip = React.forwardRef<
  HTMLSpanElement,
  VariableChipProps
>(
  (
    { varName, currentValue, allValues, onValueChange, isDisabled = false },
    ref,
  ) => {
    const [isOpen, setIsOpen] = React.useState(false);

    const handleSelect = (value: string) => {
      onValueChange(varName, value);
      setIsOpen(false);
    };

    return (
      <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
        <DropdownMenuTrigger asChild disabled={isDisabled}>
          <span
            ref={ref}
            data-variable={varName}
            contentEditable={false}
            className={cn(
              'vai-inline vai-bg-orange-100 vai-text-orange-600',
              'vai-px-1 vai-cursor-pointer vai-select-none',
              'hover:vai-bg-orange-200 vai-transition-colors',
              isDisabled && 'vai-opacity-50 vai-cursor-not-allowed',
            )}
          >
            {currentValue}
          </span>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="start"
          className="vai-min-w-[150px]"
          onCloseAutoFocus={(e) => e.preventDefault()}
        >
          {allValues.map((value) => (
            <DropdownMenuItem
              key={value}
              onClick={() => handleSelect(value)}
              className="vai-flex vai-items-center vai-gap-2"
            >
              <Check
                className={cn(
                  'vai-h-4 vai-w-4 vai-shrink-0',
                  value !== currentValue && 'vai-invisible',
                )}
              />
              <span>{value}</span>
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  },
);
